import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (!reviews.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, [reviews]);

  if (!reviews.length) return null;
  const r = reviews[idx];

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="reviews-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <div className="flex items-center gap-4 justify-center mb-4">
          <span className="divider-gold" />
          <span className="overline">The Salon Diary</span>
          <span className="divider-gold" />
        </div>
        <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light leading-[1.05] mb-14">
          Words from <em className="gold-text not-italic">our circle</em>.
        </h2>

        <div className="relative">
          <Quote size={56} className="text-[#C9A961]/30 mx-auto mb-6" strokeWidth={1} />
          <AnimatePresence mode="wait">
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.5 }}
              data-testid="review-card"
            >
              <p className="font-serif-display italic text-2xl md:text-3xl leading-[1.4] text-black/85 max-w-3xl mx-auto">
                "{r.text}"
              </p>
              <div className="flex flex-col items-center mt-8">
                <img src={r.image} alt={`Review by ${r.name}`} loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#C9A961]/30" />
                <div className="mt-3 font-medium" data-testid="review-name">{r.name}</div>
                <div className="overline text-[10px] mt-1">{r.role}</div>
                <div className="flex items-center gap-1 mt-3">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={12} fill="#C9A961" strokeWidth={0} color="#C9A961" />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-4 mt-12">
            <button
              onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
              className="w-11 h-11 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              data-testid="review-prev"
              aria-label="Previous"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-[2px] transition-all ${i === idx ? "w-8 bg-[#C9A961]" : "w-4 bg-black/15"}`}
                  data-testid={`review-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={() => setIdx((i) => (i + 1) % reviews.length)}
              className="w-11 h-11 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              data-testid="review-next"
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
